/**
 * Inventory prototype: one dialog exercising every inventory endpoint - create, list by category,
 * adjust (in units or packs), variation settings, the reorder list, backorders, barcode lookup (typed
 * or from a USB scanner, in the Stock search box), and archiving and restoring - a whole item, or one
 * variation of it. Stock is adjusted, a variation's settings edited and a variation archived in a side
 * panel opened from any table row.
 *
 * Searches and category loads come back a page at a time and without variations; "Load more"
 * fetches the next page, and opening a row in the Stock table reads that item in full. Variations
 * also arrive from creating an item, a barcode lookup or the reorder list. Everything seen is merged
 * into a working set for the life of the dialog, which is what the Stock table shows. Nothing is
 * kept once the dialog closes.
 *
 * A contact lens item is the lens - its maker, material, design and replacement, started from the
 * practice's lens catalogue - and each variation one box prescription (sphere, cylinder, axis, add,
 * base curve, diameter), labelled by it. Most have none in stock: the practice orders them in for the
 * patient, and a contact lens prescription can only name a box listed here, so a lens takes new box
 * prescriptions after it is created.
 */
app.controller("inventoryDashboardController", [
	"$scope",
	"$mdDialog",
	"$timeout",
	"$window",
	"Session",
	"InventoryAPI",
	"ExceptionAPI",
	"PUSH_EVENTS",
	"LiveReloadService",
	"LiveUpdateGuard",
	"LensDispensingStore",
	function ($scope, $mdDialog, $timeout, $window, Session, InventoryAPI, ExceptionAPI, PUSH_EVENTS, LiveReloadService,
		LiveUpdateGuard, Lens) {
		const MAX_VARIATIONS = 49;
		// A contact lens gains a box prescription for each one it's ordered in.
		const MAX_CONTACT_LENS_VARIATIONS = 500;

		$scope.practiceName = Session.practice ? Session.practice.name : "";
		$scope.busy = false;
		$scope.flash = null;

		// Backorders are their own permissions: seeing what is owed, and moving one on.
		const canViewBackorders = Session.hasPermission("PRACTICE_INVENTORY_BACKORDER_VIEW");
		$scope.canManageBackorders = Session.hasPermission("PRACTICE_INVENTORY_BACKORDER_MANAGE");

		// So are restoring an item, and archiving or restoring one variation of it. Archiving a whole
		// item comes with managing the stock.
		$scope.canRestoreItems = Session.hasPermission("PRACTICE_INVENTORY_ITEM_RESTORE");
		$scope.canArchiveVariations = Session.hasPermission("PRACTICE_INVENTORY_VARIATION_ARCHIVE");
		$scope.canRestoreVariations = Session.hasPermission("PRACTICE_INVENTORY_VARIATION_RESTORE");

		$scope.sections = [
			{ key: "stock", label: "Stock", icon: "fa-cubes" },
			{ key: "reorder", label: "Reorder list", icon: "fa-exclamation-triangle" },
			{ key: "backorders", label: "Backorders", icon: "fa-clock-o", visible: canViewBackorders },
			{ key: "create", label: "New item", icon: "fa-plus" },
		].filter((s) => s.visible !== false);
		$scope.section = "stock";

		$scope.showSection = function (key) {
			$scope.section = key;
			$scope.flash = null;
			$scope.closePanel();
			if (key === "reorder") {
				loadReorderList();
			}
			if (key === "backorders") {
				loadBackorders();
			}
		};

		$scope.cancel = function () {
			$mdDialog.cancel();
		};

		// =================================================================
		// Working set
		// =================================================================

		const itemsById = {};
		$scope.workingItems = [];
		$scope.stockFilter = { text: "" };

		/**
		 * What the Stock table lists: the items the last search, category load or barcode lookup
		 * returned. The working set holds more - the reorder list loads as the dialog opens - and
		 * those are kept for their variations, but never listed as if the search had found them.
		 */
		$scope.results = { ids: [], label: "" };
		$scope.stockRows = [];

		/** Starts a new set of results, e.g. "in Frames", 'matching "avi"', "with barcode 941...". */
		function showResults(label) {
			$scope.results = { ids: [], label: label };
			refreshStockRows();
		}

		function addResults(list) {
			(Array.isArray(list) ? list : []).forEach(function (raw) {
				if (raw && raw.item_id && $scope.results.ids.indexOf(raw.item_id) === -1) {
					$scope.results.ids.push(raw.item_id);
				}
			});
			refreshStockRows();
		}

		function byName(a, b) {
			return (a.name || "").localeCompare(b.name || "");
		}

		function refreshStockRows() {
			$scope.stockRows = $scope.results.ids.map(id => itemsById[id]).filter(Boolean).sort(byName);
		}

		function mergeItems(list) {
			(Array.isArray(list) ? list : []).forEach(function (raw) {
				if (!raw || !raw.item_id) {
					return;
				}
				let item = itemsById[raw.item_id];
				if (!item) {
					item = itemsById[raw.item_id] = { variations: [] };
				}
				// A response carries only some of an item's variations (the reorder list and a
				// barcode lookup return just the matching ones), so they are merged, never replaced.
				angular.extend(item, raw, { variations: item.variations });
				(raw.variations || []).forEach(mergeVariation);
			});
			rebuildWorkingSet();
		}

		function mergeVariation(raw) {
			const item = raw && itemsById[raw.item_id];
			if (!item) {
				return;
			}
			const existing = item.variations.find(v => v.variation_id === raw.variation_id);
			if (existing) {
				angular.extend(existing, raw);
			} else {
				item.variations.push(angular.copy(raw));
			}
		}

		/** Colour then size, ignoring case, blanks first - the order the server reads an item in. */
		function compareVariations(a, b) {
			const byText = (x, y) => (x || "").localeCompare(y || "", undefined, { sensitivity: "base" });
			return byText(a.color, b.color) || byText(a.size, b.size);
		}

		function rebuildWorkingSet() {
			$scope.workingItems = Object.keys(itemsById).map(id => itemsById[id]).sort(byName);

			// Variations arrive in pieces from different sections, so they are put back in order here.
			$scope.workingItems.forEach(item => item.variations.sort(compareVariations));
			refreshStockRows();
		}

		// Narrows the listed results without another request. Matches the way the server searches -
		// every word, anywhere, any case.
		$scope.filteredItems = function () {
			const words = ($scope.stockFilter.text || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
			if (!words.length) {
				return $scope.stockRows;
			}
			return $scope.stockRows.filter(function (item) {
				const searchable = `${item.name || ""} ${$scope.categoryLabel(item)}`.toLowerCase();
				return words.every(word => searchable.indexOf(word) !== -1);
			});
		};

		// =================================================================
		// Paging: the last search or category load, and "Load more"
		// =================================================================

		/**
		 * The last search or category load. fetch(pageIndexes) repeats it from a cursor; pageIndexes
		 * holds the cursor the server sent with the latest page, and is empty once the last page is
		 * in. loaded counts the items received so far - the server never sends a total.
		 */
		$scope.listing = null;

		function startListing(listing) {
			$scope.listing = angular.extend(listing, { pageIndexes: [], loaded: 0, pagesLoaded: 0, loading: false });
			showResults(listing.label);
			return loadNextPage();
		}

		function loadNextPage() {
			const listing = $scope.listing;
			const firstPage = listing.pagesLoaded === 0;
			listing.loading = true;

			const onPage = function (resp) {
				const data = resp.data || {};
				const items = Array.isArray(data.items) ? data.items : [];
				mergeItems(items);
				// A listing replaced while this page was on its way keeps its items in the working
				// set, but no longer lists them or reports on them.
				if ($scope.listing !== listing) {
					return;
				}
				addResults(items);
				listing.pageIndexes = Array.isArray(data.page_indexes) ? data.page_indexes : [];
				listing.loaded += items.length;
				listing.pagesLoaded++;
			};

			// Later pages load as the table is scrolled, so they stay in the background: they neither
			// block the dialog nor clear the message on screen.
			const request = listing.fetch(listing.pageIndexes);
			const done = firstPage
				? run(request, onPage, listing.failMessage)
				: request.then(onPage).catch(resp => fail(resp, listing.failMessage));
			return done.finally(function () {
				listing.loading = false;
			});
		}

		/**
		 * The first page of a search or category load is on its way, so the table is held back until
		 * it lands. Later pages load under the table instead.
		 */
		$scope.isFirstPageLoading = function () {
			return !!$scope.listing && $scope.listing.loading && $scope.listing.pagesLoaded === 0;
		};

		$scope.isNextPageLoading = function () {
			return !!$scope.listing && $scope.listing.loading && $scope.listing.pagesLoaded > 0;
		};

		$scope.hasMorePages = function () {
			return !!$scope.listing && $scope.listing.pageIndexes.length > 0;
		};

		/** Called as the end of the table scrolls into view, so it must ignore a page already on its way. */
		$scope.loadMore = function () {
			if ($scope.hasMorePages() && !$scope.listing.loading) {
				loadNextPage();
			}
		};

		// =================================================================
		// Active or archived: which items the Stock lists and searches show
		// =================================================================

		$scope.stockStatus = { value: InventoryAPI.STATUS.ACTIVE };
		$scope.stockStatusOptions = [
			{ value: InventoryAPI.STATUS.ACTIVE, label: "Active items" },
			{ value: InventoryAPI.STATUS.ARCHIVED, label: "Archived items" },
		];

		/** "active" or "archived", for the items the current listing shows. */
		$scope.statusWord = function () {
			return $scope.listing && $scope.listing.status === InventoryAPI.STATUS.ARCHIVED ? "archived" : "active";
		};

		/** Lists the other status the same way: the last search again, or else the category. */
		$scope.changeStockStatus = function () {
			if ($scope.listing && $scope.listing.term) {
				runSearch($scope.listing.term);
			} else {
				$scope.loadCategory();
			}
		};

		/** Whether an item is one the listing would return: active, or archived when listing those. */
		function hasListedStatus(item, status) {
			return $scope.isArchived(item) === (status === InventoryAPI.STATUS.ARCHIVED);
		}

		// =================================================================
		// Search: by name, or by barcode in the same box
		// =================================================================

		/**
		 * Digits only, six or more: UPC-E and longer, EAN-8/13, GTIN-14. A scanner types the code and
		 * presses Enter, so the one box takes both - no switching modes between a scan and a name.
		 */
		const BARCODE_LIKE = /^\d{6,}$/;

		$scope.searchForm = { term: "" };

		/** The barcode the last search found nothing for, offered to the New item form. */
		$scope.barcodeMiss = null;

		$scope.searchItems = function () {
			const term = ($scope.searchForm.term || "").trim();
			if (!term) {
				return;
			}
			$scope.barcodeMiss = null;
			if (BARCODE_LIKE.test(term)) {
				findByBarcode(term);
				return;
			}
			if (term.length < 2) {
				$scope.flash = { type: "error", text: "Enter at least 2 characters to search for." };
				return;
			}
			runSearch(term);
		};

		function runSearch(term) {
			const status = $scope.stockStatus.value;
			$scope.stockFilter.text = "";
			startListing({
				label: `matching "${term}"`,
				term: term,
				status: status,
				fetch: pageIndexes => InventoryAPI.searchItems(term, pageIndexes, status),
				// As the server searches: the status listed, every word somewhere in the name or Other category name.
				matches: function (item) {
					const searchable = `${item.name || ""} ${item.other_category || ""}`.toLowerCase();
					return hasListedStatus(item, status)
						&& term.toLowerCase().split(/\s+/).filter(Boolean).every(word => searchable.indexOf(word) !== -1);
				},
				failMessage: "Could not search the inventory.",
			});
		}

		/**
		 * Looks a barcode up and shows the items holding it, archived ones included. One match - the
		 * usual case - opens straight in the side panel, so scanning a product and adjusting it is
		 * one step; the box is cleared for the next scan.
		 */
		function findByBarcode(code) {
			run(InventoryAPI.getItemsByBarcode(code), function (resp) {
				const items = Array.isArray(resp.data) ? resp.data : [];
				// Shown as a notice with a way to create the item, rather than as an error. The code is
				// left in the box to correct, but selected, so the next scan replaces it.
				if (!items.length) {
					$scope.barcodeMiss = code;
					$timeout(function () {
						const input = $window.document.getElementById("invSearch");
						if (input) {
							input.select();
						}
					});
					return;
				}

				// A barcode lookup is not paged, so it ends any listing's "Load more".
				mergeItems(items);
				$scope.listing = null;
				showResults(`with barcode ${code}`);
				addResults(items);
				$scope.stockFilter.text = "";
				$scope.searchForm.term = "";

				const matches = [];
				items.forEach(raw => (raw.variations || []).forEach(v => matches.push({
					item: itemsById[raw.item_id],
					variation: itemsById[raw.item_id].variations.find(known => known.variation_id === v.variation_id),
				})));
				matches.forEach(match => expandItem(match.item));

				if (matches.length === 1) {
					$scope.openPanel(matches[0].item, matches[0].variation);
					succeed(`Found barcode ${code}: ${matches[0].item.name}, ${$scope.variationLabel(matches[0].variation)}.`);
				} else {
					succeed(`Barcode ${code} is on ${matches.length} variations. Open one to adjust it.`);
				}
			}, "Could not look up that barcode.");
		}

		$scope.CATEGORY = InventoryAPI.CATEGORY;
		// Offered in their groups (Optical, Ophthalmology); the retired Sundries is not among them.
		$scope.categoryOptions = [];
		InventoryAPI.CATEGORY_GROUPS.forEach(function (group) {
			group.categories.forEach(function (category) {
				$scope.categoryOptions.push({
					value: category,
					label: InventoryAPI.CATEGORY_LABELS[category],
					group: group.label,
				});
			});
		});

		/**
		 * The Stock filter's choice of every category. Only the filter offers it - an item still needs
		 * a real category - and it is sent to the API as no category at all.
		 */
		const ALL_CATEGORIES = "ALL";
		$scope.ALL_CATEGORIES = ALL_CATEGORIES;
		$scope.filterCategoryOptions = [{ value: ALL_CATEGORIES, label: "All" }].concat($scope.categoryOptions);

		/** "Frames", "Sundries", or an Other item's own category name. */
		$scope.categoryLabel = function (item) {
			if (!item) {
				return "";
			}
			if (item.category === InventoryAPI.CATEGORY.OTHER) {
				return item.other_category || InventoryAPI.CATEGORY_LABELS.OTHER;
			}
			return InventoryAPI.CATEGORY_LABELS[item.category] || item.category || "";
		};

		$scope.knownOtherCategories = function () {
			const seen = {};
			$scope.workingItems.forEach(item => {
				if (item.category === InventoryAPI.CATEGORY.OTHER && item.other_category) {
					seen[item.other_category] = true;
				}
			});
			return Object.keys(seen).sort();
		};

		// A contact lens box by its prescription; anything else by its colour and size.
		$scope.variationLabel = function (variation) {
			if (variation.cl_sphere !== null && variation.cl_sphere !== undefined) {
				return Lens.clRxText(Lens.clRxOf(variation));
			}
			const parts = [variation.color, variation.size].filter(Boolean);
			return parts.length ? parts.join(" / ") : "Standard";
		};

		$scope.isContacts = (category) => category === InventoryAPI.CATEGORY.CONTACT_LENSES;
		$scope.maxVariationsFor = (category) => ($scope.isContacts(category) ? MAX_CONTACT_LENS_VARIATIONS : MAX_VARIATIONS);

		// A contact lens item's description in a line: "CooperVision · comfilcon A · Toric · Monthly".
		$scope.lensDescription = function (item) {
			const design = (Lens.CL_DESIGNS.find((d) => d.key === item.cl_design) || {}).label;
			const modality = item.cl_modality ? Lens.modalityMeta(item.cl_modality).label : null;
			return [item.cl_maker, item.cl_material, design, modality].filter(Boolean).join(" · ");
		};

		$scope.isArchived = function (item) {
			return !!item && item.status === InventoryAPI.STATUS.ARCHIVED;
		};

		/** Archived on its own, while the rest of its item may still be active. */
		$scope.isVariationArchived = function (variation) {
			return !!variation && variation.status === InventoryAPI.STATUS.ARCHIVED;
		};

		/** What one variation of the item is called: a contact lens's are its box prescriptions. */
		$scope.variationNoun = function (item) {
			return item && $scope.isContacts(item.category) ? "box prescription" : "variation";
		};

		/** "26 units (2 packs + 2)" when the variation is bought in packs. */
		$scope.packBreakdown = function (variation) {
			const perPack = variation.units_per_pack;
			if (!perPack || perPack <= 1 || !variation.quantity) {
				return "";
			}
			const packs = Math.floor((variation.quantity || 0) / perPack);
			const rest = (variation.quantity || 0) % perPack;
			return `${packs} pack${packs === 1 ? "" : "s"}${rest ? " + " + rest : ""} of ${perPack}`;
		};

		$scope.shortfall = function (variation) {
			if (variation.min_quantity === null || variation.min_quantity === undefined) {
				return 0;
			}
			return Math.max(variation.min_quantity - (variation.quantity || 0), 0);
		};

		// =================================================================
		// Messages
		// =================================================================

		function succeed(text) {
			$scope.flash = { type: "success", text: text };
		}

		function fail(resp, fallback) {
			$scope.flash = { type: "error", text: ExceptionAPI.serverMessage(resp) || fallback };
		}

		function run(promise, onSuccess, fallback) {
			$scope.busy = true;
			$scope.flash = null;
			return promise
				.then(onSuccess)
				.catch(resp => fail(resp, fallback))
				.finally(() => {
					$scope.busy = false;
				});
		}

		// =================================================================
		// Stock: load a category, archive
		// =================================================================

		// Frames is what the dialog opens on - see the load at the bottom of the controller.
		$scope.categoryForm = {
			category: ALL_CATEGORIES,
			otherCategory: "",
		};

		/** Loads the category picked in the Stock filter, and for Other the name typed beside it. */
		$scope.loadCategory = function () {
			const form = $scope.categoryForm;
			const isAll = form.category === ALL_CATEGORIES;
			const isOther = form.category === InventoryAPI.CATEGORY.OTHER;
			const name = isOther ? (form.otherCategory || "").trim() : "";
			const label = isAll
				? "all categories"
				: isOther
					? (name || "Other (every name)")
					: InventoryAPI.CATEGORY_LABELS[form.category];

			$scope.barcodeMiss = null;
			const listedCategory = isAll ? null : form.category;
			const status = $scope.stockStatus.value;
			startListing({
				label: isAll ? "across all categories" : `in ${label}`,
				status: status,
				fetch: pageIndexes => InventoryAPI.getItemsByCategory(listedCategory, name || null, pageIndexes, null, status),
				// As the server lists them: the status listed, in the category, an Other name matched ignoring case.
				matches: item => hasListedStatus(item, status)
					&& (!listedCategory || item.category === listedCategory)
					&& (!name || (item.other_category || "").trim().toLowerCase() === name.toLowerCase()),
				failMessage: "Could not load that category.",
			});
		};

		// =================================================================
		// Stock table: expanding a row, totals and stock status
		// =================================================================

		/** Item ids whose rows are open. Kept apart from the items, which are rebuilt on every merge. */
		const expanded = {};

		$scope.isExpanded = function (item) {
			return !!expanded[item.item_id];
		};

		/**
		 * Opens or closes an item's row. Listings and searches leave variations out, so the first
		 * open reads the item in full; until then the row shows only what other sections loaded.
		 */
		$scope.toggleItem = function (item) {
			if (expanded[item.item_id]) {
				expanded[item.item_id] = false;
			} else {
				expandItem(item);
			}
		};

		function expandItem(item) {
			expanded[item.item_id] = true;
			if (!item.variationsComplete && !item.loadingVariations) {
				loadItemVariations(item);
			}
		}

		$scope.retryVariations = loadItemVariations;

		/** Loads in the row rather than through run(), so it neither blocks the dialog nor replaces its message. */
		function loadItemVariations(item) {
			item.loadingVariations = true;
			item.variationsError = null;
			return InventoryAPI.getItem(item.item_id)
				.then(function (resp) {
					mergeItems([resp.data]);
					item.variationsComplete = true;
				})
				.catch(function (resp) {
					item.variationsError = ExceptionAPI.serverMessage(resp) || "Could not load the variations.";
				})
				.finally(function () {
					item.loadingVariations = false;
				});
		}

		$scope.totalQuantity = function (item) {
			return item.variations.reduce((sum, variation) => sum + (variation.quantity || 0), 0);
		};

		// Shared objects, so a status can be compared and bound without a new object every digest.
		const STATUS = {
			ARCHIVED: { key: "archived", label: "Archived", icon: "fa-archive" },
			OUT: { key: "out", label: "Out of stock", icon: "fa-times-circle" },
			LOW: { key: "low", label: "Low stock", icon: "fa-exclamation-triangle" },
			OK: { key: "ok", label: "In stock", icon: "fa-check-circle" },
			// A contact lens box with none on hand and no minimum: ordered in for the patient, as most are.
			ORDER: { key: "order", label: "Order in", icon: "fa-truck" },
		};

		$scope.variationStatus = function (item, variation) {
			if ($scope.isArchived(item) || $scope.isVariationArchived(variation)) {
				return STATUS.ARCHIVED;
			}
			if ((variation.quantity || 0) <= 0) {
				const kept = variation.min_quantity !== null && variation.min_quantity !== undefined;
				return $scope.isContacts(item.category) && !kept ? STATUS.ORDER : STATUS.OUT;
			}
			return $scope.shortfall(variation) > 0 ? STATUS.LOW : STATUS.OK;
		};

		/**
		 * The item's worst active variation. Before its variations are read in full, only a variation
		 * known to need reordering (from the reorder list or a scan) can be reported; otherwise null.
		 */
		$scope.itemStatus = function (item) {
			if ($scope.isArchived(item)) {
				return STATUS.ARCHIVED;
			}
			const statuses = InventoryAPI.activeVariations(item).map(variation => $scope.variationStatus(item, variation));
			// A box to order in is as it should be, not a shortage.
			const fine = status => status === STATUS.OK || status === STATUS.ORDER;
			if (!item.variationsComplete) {
				return statuses.some(status => !fine(status)) ? STATUS.LOW : null;
			}
			if (statuses.length && statuses.every(status => status === STATUS.OUT)) {
				return STATUS.OUT;
			}
			if (statuses.some(status => !fine(status))) {
				return STATUS.LOW;
			}
			return statuses.length && statuses.every(status => status === STATUS.ORDER) ? STATUS.ORDER : STATUS.OK;
		};

		// Archiving is confirmed in the open row, and only offered once every variation is known, so the
		// confirmation can say what stock it writes off. Restoring needs no confirmation: it takes nothing away.
		let confirmingArchiveId = null;

		$scope.canArchive = function (item) {
			return !!item.variationsComplete;
		};

		$scope.isConfirmingArchive = function (item) {
			return confirmingArchiveId === item.item_id;
		};

		$scope.askArchive = function (item) {
			confirmingArchiveId = item.item_id;
		};

		$scope.cancelArchive = function () {
			confirmingArchiveId = null;
		};

		$scope.archiveItem = function (item) {
			const writeOff = $scope.totalQuantity(item);
			run(InventoryAPI.archiveItem(item.item_id, writeOff !== 0), function (resp) {
				markSavedHere(item.item_id);
				mergeItems([resp.data]);
				// The item comes back without its variations; what was written off is known to be gone.
				item.variations.forEach(variation => (variation.quantity = 0));
				confirmingArchiveId = null;
				succeed(writeOff
					? `${item.name} has been archived, and its ${writeOff} in stock written off.`
					: `${item.name} has been archived.`);
				loadReorderList();
			}, "Could not archive the item.");
		};

		$scope.restoreItem = function (item) {
			run(InventoryAPI.restoreItem(item.item_id), function (resp) {
				markSavedHere(item.item_id);
				mergeItems([resp.data]);
				item.variationsComplete = true;
				succeed(`${item.name} has been restored. It has no stock until some is added.`);
				loadReorderList();
			}, "Could not restore the item.");
		};

		// =================================================================
		// Side panel: one variation's stock and details
		// =================================================================

		/**
		 * The variation open in the side panel, with its two forms. The panel keeps its own saving
		 * flag and message, so a failed save is reported beside the form that caused it.
		 */
		$scope.panel = null;

		/** Recorded on the audit entry, ahead of any note, so every change says why it was made. */
		$scope.ADJUST_REASONS = [
			"Received delivery",
			"Sold or dispensed",
			"Damaged or expired",
			"Returned to supplier",
			"Stocktake correction",
			"Other",
		];

		let panelOpener = null;

		/**
		 * @param {Object} [preset] - Starting values for the adjust form, e.g. the reorder shortfall.
		 */
		$scope.openPanel = function (item, variation, preset) {
			// Focus goes back to the row that opened the panel when it closes.
			if (!$scope.panel) {
				panelOpener = $window.document.activeElement;
			}
			$scope.panel = {
				item: item,
				variation: variation,
				adjust: angular.extend({ change: null, unit: "units", reason: null, note: "" }, preset || {}),
				details: detailsOf(variation),
				message: null,
				saving: false,
				confirmingArchive: false,
			};
			$timeout(function () {
				const title = $window.document.getElementById("invPanelTitle");
				if (title) {
					title.focus();
				}
			});
		};

		$scope.closePanel = function () {
			if (!$scope.panel) {
				return;
			}
			$scope.panel = null;
			if (panelOpener && $window.document.body.contains(panelOpener)) {
				panelOpener.focus();
			}
			panelOpener = null;
		};

		$scope.isSelected = function (variation) {
			return !!$scope.panel && $scope.panel.variation.variation_id === variation.variation_id;
		};

		// Escape closes the panel only - stopped here, so it does not also close the dialog.
		$scope.onPanelKey = function (event) {
			if (event.keyCode === 27) {
				event.preventDefault();
				event.stopPropagation();
				$scope.closePanel();
			}
		};

		function detailsOf(variation) {
			return {
				min_quantity: variation.min_quantity,
				units_per_pack: variation.units_per_pack,
				location_name: variation.location_name || "",
				barcode: variation.barcode || "",
			};
		}

		/** The details as the server takes them: every field, a blank one cleared. */
		function detailsPayload(details) {
			return {
				min_quantity: toNumberOrNull(details.min_quantity),
				units_per_pack: toNumberOrNull(details.units_per_pack),
				location_name: toTextOrNull(details.location_name),
				barcode: toTextOrNull(details.barcode),
			};
		}

		$scope.detailsChanged = function () {
			const panel = $scope.panel;
			return !!panel && !angular.equals(detailsPayload(panel.details), detailsPayload(detailsOf(panel.variation)));
		};

		/** The signed change entered, converted to single units. */
		$scope.unitsOf = function (form, variation) {
			const amount = parseInt(form.change, 10) || 0;
			const perPack = form.unit === "packs" && variation.units_per_pack > 1 ? variation.units_per_pack : 1;
			return amount * perPack;
		};

		$scope.stepChange = function (step) {
			const adjust = $scope.panel.adjust;
			adjust.change = (parseInt(adjust.change, 10) || 0) + step;
		};

		/** The quantity once the change is saved, for the "2 → 5" preview. */
		$scope.quantityAfter = function () {
			const panel = $scope.panel;
			return (panel.variation.quantity || 0) + $scope.unitsOf(panel.adjust, panel.variation);
		};

		/** Why the adjust form cannot be saved yet, or null when it can. */
		$scope.adjustProblem = function () {
			const panel = $scope.panel;
			if (!$scope.unitsOf(panel.adjust, panel.variation)) {
				return "Enter how many to add or remove.";
			}
			if ($scope.quantityAfter() < 0) {
				return `Only ${panel.variation.quantity} in stock.`;
			}
			if (!panel.adjust.reason) {
				return "Choose a reason.";
			}
			return null;
		};

		function runInPanel(promise, onSuccess, fallback) {
			const panel = $scope.panel;
			panel.saving = true;
			panel.message = null;
			return promise
				.then(onSuccess)
				.catch(function (resp) {
					panel.message = { type: "error", text: ExceptionAPI.serverMessage(resp) || fallback };
				})
				.finally(function () {
					panel.saving = false;
				});
		}

		$scope.submitAdjust = function () {
			const panel = $scope.panel;
			if ($scope.adjustProblem()) {
				return;
			}
			const units = $scope.unitsOf(panel.adjust, panel.variation);
			const note = toTextOrNull(panel.adjust.note);
			const notes = note ? `${panel.adjust.reason}: ${note}` : panel.adjust.reason;

			runInPanel(InventoryAPI.adjustQuantity(panel.item.item_id, panel.variation.variation_id, units, notes),
				function (resp) {
					markSavedHere(panel.item.item_id);
					mergeVariation(resp.data);
					rebuildWorkingSet();
					panel.adjust = { change: null, unit: "units", reason: null, note: "" };
					panel.message = {
						type: "success",
						text: `${units > 0 ? "Added" : "Removed"} ${Math.abs(units)}. ${resp.data.quantity} now in stock.`,
					};
					loadReorderList();
				}, "Could not adjust the stock.");
		};

		$scope.submitDetails = function () {
			const panel = $scope.panel;
			// Every setting is replaced, so a contact lens box keeps its prescription by sending it back.
			const box = $scope.isContacts(panel.item.category) ? Lens.clRxFields(Lens.clRxOf(panel.variation)) : {};
			runInPanel(InventoryAPI.updateVariationDetails(panel.item.item_id, panel.variation.variation_id,
				angular.extend(detailsPayload(panel.details), box)),
				function (resp) {
					markSavedHere(panel.item.item_id);
					mergeVariation(resp.data);
					rebuildWorkingSet();
					panel.details = detailsOf(panel.variation);
					panel.message = { type: "success", text: "Details saved." };
					loadReorderList();
				}, "Could not save the details.");
		};

		$scope.resetDetails = function () {
			$scope.panel.details = detailsOf($scope.panel.variation);
		};

		/**
		 * Whether the panel's variation is the last active one of its item, which can't be archived on
		 * its own - the item can. Only known once the item has been read in full; until then the server
		 * says so if it is.
		 */
		$scope.isLastActiveVariation = function () {
			const panel = $scope.panel;
			return !!panel && !!panel.item.variationsComplete && InventoryAPI.activeVariations(panel.item).length <= 1;
		};

		$scope.askArchiveVariation = function () {
			$scope.panel.confirmingArchive = true;
		};

		$scope.cancelArchiveVariation = function () {
			$scope.panel.confirmingArchive = false;
		};

		/** Archives the panel's variation, writing off whatever stock it has, as its confirmation said. */
		$scope.archiveVariation = function () {
			const panel = $scope.panel;
			const writeOff = panel.variation.quantity || 0;
			runInPanel(InventoryAPI.archiveVariation(panel.item.item_id, panel.variation.variation_id, writeOff !== 0),
				function (resp) {
					markSavedHere(panel.item.item_id);
					mergeVariation(resp.data);
					rebuildWorkingSet();
					panel.confirmingArchive = false;
					panel.message = {
						type: "success",
						text: writeOff
							? `Archived, and its ${writeOff} in stock written off. It can't be stocked or sold until it is restored.`
							: "Archived. It can't be stocked or sold until it is restored.",
					};
					loadReorderList();
				}, "Could not archive the variation.");
		};

		$scope.restoreVariation = function () {
			const panel = $scope.panel;
			runInPanel(InventoryAPI.restoreVariation(panel.item.item_id, panel.variation.variation_id),
				function (resp) {
					markSavedHere(panel.item.item_id);
					mergeVariation(resp.data);
					rebuildWorkingSet();
					panel.details = detailsOf(panel.variation);
					panel.message = { type: "success", text: "Restored. It has no stock until some is added." };
					loadReorderList();
				}, "Could not restore the variation.");
		};

		// =================================================================
		// Reorder list
		// =================================================================

		$scope.reorder = { loading: false, loaded: false, items: [], count: 0 };

		function loadReorderList() {
			$scope.reorder.loading = true;
			return InventoryAPI.getItemsBelowMinimum()
				.then(function (resp) {
					const items = Array.isArray(resp.data) ? resp.data : [];
					mergeItems(items);
					// Rows point at the working-set copies, so an adjustment updates them in place.
					$scope.reorder.items = items.map(raw => ({
						item: itemsById[raw.item_id],
						variations: (raw.variations || []).map(v =>
							itemsById[raw.item_id].variations.find(known => known.variation_id === v.variation_id)),
					}));
					$scope.reorder.count = items.reduce((sum, raw) => sum + (raw.variations || []).length, 0);
					$scope.reorder.loaded = true;
				})
				.catch(function (resp) {
					if ($scope.section === "reorder") {
						fail(resp, "Could not load the reorder list.");
					}
				})
				.finally(function () {
					$scope.reorder.loading = false;
				});
		}

		// =================================================================
		// Backorders: what a sale couldn't take from stock, owed to a patient
		// =================================================================

		// Only the open ones are listed: one that has arrived has gone to the patient and drops off.
		$scope.backorders = { loading: false, loaded: false, list: [], count: 0 };
		$scope.editingBackorder = null;

		$scope.BACKORDER_STEPS = [
			{ status: "PENDING", label: "Pending", icon: "fa-clock-o" },
			{ status: "ORDERED", label: "Ordered", icon: "fa-truck" },
			{ status: "ARRIVED", label: "Arrived", icon: "fa-check" },
		];
		const stepIndex = (status) => $scope.BACKORDER_STEPS.findIndex((step) => step.status === status);
		$scope.backorderStep = (backorder) => $scope.BACKORDER_STEPS[Math.max(0, stepIndex(backorder.status))];
		$scope.isStepDone = (backorder, step) => stepIndex(step.status) < stepIndex(backorder.status);

		function setBackorders(list) {
			$scope.backorders.list = list;
			$scope.backorders.count = list.length;
		}

		function loadBackorders() {
			if (!canViewBackorders) {
				return;
			}
			$scope.backorders.loading = true;
			return InventoryAPI.getBackorders(0)
				.then(function (resp) {
					setBackorders((Array.isArray(resp.data) ? resp.data : []).filter((b) => b.status === "PENDING" || b.status === "ORDERED"));
					$scope.backorders.loaded = true;
				})
				.catch(function (resp) {
					if ($scope.section === "backorders") {
						fail(resp, "Could not load the backorders.");
					}
				})
				.finally(function () {
					$scope.backorders.loading = false;
				});
		}

		// Back to pending happens at once. Ordered takes a note of where from; arrived is confirmed, since
		// it takes the backorder off the list.
		$scope.chooseBackorderStatus = function (backorder, status) {
			if (backorder.saving || (status === backorder.status && status !== "ORDERED")) {
				return;
			}
			if (status === "PENDING") {
				saveBackorderStatus(backorder, status);
				return;
			}
			$scope.editingBackorder = { recordId: backorder.record_id, status: status, note: backorder.note || "" };
		};
		$scope.isEditingBackorder = (backorder) => !!$scope.editingBackorder && $scope.editingBackorder.recordId === backorder.record_id;
		$scope.stopEditingBackorder = () => ($scope.editingBackorder = null);

		$scope.confirmBackorderStatus = function (backorder) {
			const editing = $scope.editingBackorder;
			if (!editing) {
				return;
			}
			saveBackorderStatus(backorder, editing.status, editing.status === "ORDERED" ? (editing.note || "").trim() : undefined);
		};

		function saveBackorderStatus(backorder, status, note) {
			backorder.saving = true;
			InventoryAPI.updateBackorderStatus(backorder.record_id, status, note)
				.then(function (resp) {
					$scope.editingBackorder = null;
					if (status === "ARRIVED") {
						setBackorders($scope.backorders.list.filter((b) => b !== backorder));
						succeed(`${backorder.item_name} arrived for ${backorder.patient_name || "the patient"} - it's off the backorder list.`);
					} else {
						angular.extend(backorder, resp.data, { patient_name: backorder.patient_name });
					}
				})
				.catch(function (resp) {
					fail(resp, "Could not update the backorder.");
				})
				.finally(function () {
					backorder.saving = false;
				});
		}

		// =================================================================
		// Live updates: stock changed by other users, or in another tab
		// =================================================================

		const INVENTORY_EVENTS = [
			PUSH_EVENTS.INVENTORY_ITEM_ADDED,
			PUSH_EVENTS.INVENTORY_ITEM_UPDATED,
			PUSH_EVENTS.INVENTORY_ITEM_ARCHIVED,
			PUSH_EVENTS.INVENTORY_ITEM_RESTORED,
			PUSH_EVENTS.INVENTORY_STOCK_UPDATED,
		];

		/** The events that can bring an item into the list on screen: new, or moved between active and archived. */
		const JOINING_EVENTS = [
			PUSH_EVENTS.INVENTORY_ITEM_ADDED,
			PUSH_EVENTS.INVENTORY_ITEM_ARCHIVED,
			PUSH_EVENTS.INVENTORY_ITEM_RESTORED,
		];

		/**
		 * An item named by an event is re-read whole, which updates its rows in Stock, its variations
		 * and an open side panel in place. A change this user made is re-read too - they may have the
		 * dialog open in another tab - and only the wording of the panel's notice tells them apart.
		 */
		INVENTORY_EVENTS.forEach(function (eventName) {
			$scope.$on(eventName, function (e, pushEvent) {
				const payload = (pushEvent && pushEvent.data) || {};
				if (!payload.itemId) {
					return;
				}
				// A new, archived or restored item is read in case it belongs to the list on screen; any
				// other change only matters to an item this dialog already holds.
				const mayJoin = JOINING_EVENTS.indexOf(eventName) !== -1;
				if (itemsById[payload.itemId] || mayJoin) {
					refreshItem(payload.itemId, payload, mayJoin);
				}
			});
		});

		// The reorder list (and the count in the rail) is re-read once per burst of changes.
		LiveReloadService.subscribe($scope, {
			events: INVENTORY_EVENTS,
			isRelevant: function () {
				return true;
			},
			// Backorders change with the stock: a sale opens one, stock coming in fills it.
			reload: function () {
				loadReorderList();
				loadBackorders();
			},
			coalesceMs: 1000,
		});

		/**
		 * "reading" while an item is re-read; "again" when another event for it arrived meanwhile, and
		 * "joining" when one of those could bring it into the list on screen.
		 */
		const refreshing = {};

		/**
		 * When this dialog last saved each item, so the echo of its own save - a poll or two later - is
		 * not reported as a change from somewhere else. Comfortably longer than the poll interval.
		 */
		const ECHO_WINDOW_MS = 60000;
		const savedHere = {};

		function markSavedHere(itemId) {
			savedHere[itemId] = Date.now();
		}

		function savedHereRecently(itemId) {
			return !!savedHere[itemId] && Date.now() - savedHere[itemId] < ECHO_WINDOW_MS;
		}

		/** @param {boolean} [mayJoin] - Whether the change can bring the item into the list on screen. */
		function refreshItem(itemId, payload, mayJoin) {
			if (refreshing[itemId]) {
				refreshing[itemId] = mayJoin || refreshing[itemId] === "joining" ? "joining" : "again";
				return;
			}
			refreshing[itemId] = "reading";

			LiveUpdateGuard.run("inventory", function () {
				return InventoryAPI.getItem(itemId);
			})
				.then(function (resp) {
					mergeItems([resp.data]);
					const item = itemsById[itemId];
					item.variationsComplete = true;
					// A new, archived or restored item joins the list on screen when the list's own search
					// would have found it. One already listed stays, showing its new status, as it does
					// after a change made here.
					if (mayJoin && $scope.listing && $scope.listing.matches && $scope.listing.matches(item)) {
						addResults([item]);
					}
					noteChangeInPanel(item, payload);
				})
				// Not logged: a response can carry practice data. A failed or stopped read just leaves
				// the screen as it was until the next change.
				.catch(angular.noop)
				.finally(function () {
					const state = refreshing[itemId];
					delete refreshing[itemId];
					if (state !== "reading") {
						refreshItem(itemId, payload, state === "joining");
					}
				});
		}

		/**
		 * The panel's figures have already updated in place; this says why they moved. Forms being
		 * filled in are left alone - the adjust form's change is relative, so it still applies.
		 */
		function noteChangeInPanel(item, payload) {
			const panel = $scope.panel;
			if (!panel || panel.item !== item || panel.saving) {
				return;
			}
			if (payload.variationId && payload.variationId !== panel.variation.variation_id) {
				return;
			}
			const me = Session.user && Session.user.id;
			const byMe = me && payload.updatedByUserId && String(me) === String(payload.updatedByUserId);
			// The echo of a save made in this dialog: it already said so, in its own words.
			if (byMe && savedHereRecently(item.item_id)) {
				return;
			}
			panel.message = {
				type: "info",
				text: byMe
					? "Updated from another of your windows. The figures above are current."
					: "Just changed by someone else. The figures above are current.",
			};
		}

		// =================================================================
		// New item
		// =================================================================

		// A contact lens box's prescription fields sit alongside the rest; only a contact lens uses them.
		function blankVariation() {
			return {
				color: "", size: "", quantity: 0, min_quantity: null, barcode: "", location_name: "", units_per_pack: null,
				cl_sphere: null, cl_cylinder: null, cl_axis: null, cl_add: "", cl_base_curve: null, cl_diameter: null,
			};
		}

		function resetCreateForm() {
			$scope.createForm = {
				name: "",
				category: InventoryAPI.CATEGORY.FRAME,
				otherCategory: "",
				price: null,
				// A contact lens: what it is, and the catalogue lens it was started from.
				lens: { template: null, maker: "", material: "", design: "spherical", modality: "monthly" },
				variations: [blankVariation()],
			};
		}

		resetCreateForm();

		// Contact lens designs and replacements a stock lens can be.
		$scope.clDesigns = Lens.CL_DESIGNS.filter((d) => ["spherical", "toric", "multifocal"].indexOf(d.key) !== -1);
		$scope.clModalities = Lens.CL_MODALITIES.filter((m) => m.key !== "custom");

		// The practice's lens catalogue starts a new contact lens: its name, maker, material, design,
		// replacement, box size and price, and the base curve and diameter its boxes come in. Read when
		// first wanted; without it the lens is described by hand.
		$scope.clTemplates = [];
		let templatesRead = false;
		$scope.$watch("createForm.category", function (category) {
			if (!$scope.isContacts(category) || templatesRead) return;
			templatesRead = true;
			Lens.loadCatalogue().then((catalogue) => ($scope.clTemplates = catalogue.contactLenses.filter((c) => !c.custom)), angular.noop);
		});
		const templateName = (c) => [c.brand, c.name].filter(Boolean).join(" ");
		$scope.templateLabel = (c) => `${templateName(c)} · ${Lens.modalityMeta(c.modality).label.toLowerCase()} ${c.design}`;

		$scope.applyClTemplate = function () {
			const form = $scope.createForm;
			const c = form.lens.template;
			if (!c) return;
			angular.extend(form.lens, { maker: c.maker || c.brand || "", material: c.material || "", design: c.design, modality: c.modality });
			form.name = templateName(c);
			if (c.price) form.price = c.price;
			form.variations.forEach((v) => fitBoxToLens(v, c));
		};

		// A box starts in the lens's own base curve, diameter and box size.
		function fitBoxToLens(v, c) {
			if (!c) return;
			if (v.cl_base_curve === null && c.bcs && c.bcs.length) v.cl_base_curve = c.bcs[0];
			if (v.cl_diameter === null && c.dia) v.cl_diameter = c.dia;
			if (v.units_per_pack === null && c.pack) v.units_per_pack = c.pack;
		}

		// What the server takes for a variation: a contact lens box is labelled by its prescription.
		function variationPayload(v, contacts) {
			const box = contacts
				? Lens.clRxFields({ sph: v.cl_sphere, cyl: v.cl_cylinder, axis: v.cl_axis, add: v.cl_add, bc: v.cl_base_curve, dia: v.cl_diameter })
				: {};
			return angular.extend({
				color: contacts ? null : toTextOrNull(v.color),
				size: contacts ? Lens.clRxText(Lens.clRxOf(box)) : toTextOrNull(v.size),
				quantity: toNumberOrNull(v.quantity) || 0,
				min_quantity: toNumberOrNull(v.min_quantity),
				barcode: toTextOrNull(v.barcode),
				location_name: toTextOrNull(v.location_name),
				units_per_pack: toNumberOrNull(v.units_per_pack),
			}, box);
		}

		/** Starts the New item form with a barcode nothing in the practice has yet. */
		$scope.createFromBarcode = function (code) {
			resetCreateForm();
			$scope.createForm.variations[0].barcode = code;
			$scope.barcodeMiss = null;
			$scope.showSection("create");
		};

		// A new box carries on from the last one's fit, so only its powers need typing.
		$scope.addVariationRow = function () {
			const form = $scope.createForm;
			if (form.variations.length >= $scope.maxVariationsFor(form.category)) return;
			const v = blankVariation();
			const last = form.variations[form.variations.length - 1];
			if ($scope.isContacts(form.category) && last) {
				angular.extend(v, { cl_base_curve: last.cl_base_curve, cl_diameter: last.cl_diameter, units_per_pack: last.units_per_pack });
			}
			form.variations.push(v);
		};

		$scope.removeVariationRow = function (index) {
			if ($scope.createForm.variations.length > 1) {
				$scope.createForm.variations.splice(index, 1);
			}
		};

		$scope.submitCreate = function () {
			const form = $scope.createForm;
			const contacts = $scope.isContacts(form.category);
			const payload = {
				name: (form.name || "").trim(),
				category: form.category,
				// Only an Other item takes a name; the server refuses one on Frame or Sundry.
				other_category: form.category === InventoryAPI.CATEGORY.OTHER ? toTextOrNull(form.otherCategory) : null,
				price: form.price === null || form.price === "" ? null : Number(form.price),
				variations: form.variations.map((v) => variationPayload(v, contacts)),
			};
			if (contacts) {
				angular.extend(payload, {
					cl_maker: toTextOrNull(form.lens.maker),
					cl_material: toTextOrNull(form.lens.material),
					cl_design: form.lens.design,
					cl_modality: form.lens.modality,
					cl_catalogue_id: form.lens.template ? form.lens.template.id : null,
				});
			}

			run(InventoryAPI.createItem(payload), function (resp) {
				markSavedHere(resp.data.item_id);
				mergeItems([resp.data]);
				// Listed with what is on screen, and created with every variation, so the new row
				// opens straight away without a read.
				addResults([resp.data]);
				itemsById[resp.data.item_id].variationsComplete = true;
				expanded[resp.data.item_id] = true;
				succeed(`Created ${resp.data.name} with ${(resp.data.variations || []).length} variation(s).`);
				resetCreateForm();
				$scope.section = "stock";
				$scope.stockFilter.text = "";
				loadReorderList();
			}, "Could not create the item.");
		};

		// =================================================================
		// New box prescriptions for a contact lens
		// =================================================================

		// A contact lens is only prescribed in a box listed here, so one the practice hasn't had yet is
		// added to the lens - starting from the lens's own fit and box size, so only its powers need typing.
		$scope.canAddVariations = Session.hasPermission("PRACTICE_INVENTORY_VARIATION_ADD");
		$scope.adding = null;

		function boxLike(v) {
			return angular.extend(blankVariation(), {
				cl_base_curve: toNumberOrNull(v.cl_base_curve), cl_diameter: toNumberOrNull(v.cl_diameter), units_per_pack: v.units_per_pack || null,
			});
		}

		$scope.startAddBoxes = function (item) {
			const like = InventoryAPI.activeVariations(item)[0] || (item.variations || [])[0] || {};
			$scope.adding = { itemId: item.item_id, rows: [boxLike(like)] };
		};

		$scope.addBoxRow = function () {
			const rows = $scope.adding.rows;
			rows.push(boxLike(rows[rows.length - 1]));
		};

		$scope.removeBoxRow = function (index) {
			if ($scope.adding.rows.length > 1) {
				$scope.adding.rows.splice(index, 1);
			}
		};

		$scope.cancelAddBoxes = function () {
			$scope.adding = null;
		};

		$scope.submitAddBoxes = function (item) {
			const rows = $scope.adding.rows.map((v) => variationPayload(v, true));
			run(InventoryAPI.addVariations(item.item_id, rows), function (resp) {
				markSavedHere(item.item_id);
				mergeItems([resp.data]);
				itemsById[item.item_id].variationsComplete = true;
				succeed(`Added ${rows.length} box prescription${rows.length === 1 ? "" : "s"} to ${resp.data.name}.`);
				$scope.adding = null;
				loadReorderList();
			}, "Could not add the box prescriptions.");
		};

		// =================================================================
		// Helpers
		// =================================================================

		function toNumberOrNull(value) {
			if (value === null || value === undefined || value === "") {
				return null;
			}
			const number = Number(value);
			return isNaN(number) ? null : number;
		}

		function toTextOrNull(value) {
			const text = (value || "").toString().trim();
			return text ? text : null;
		}

		// Open on the default category's stock rather than an empty pane, and have the reorder
		// count ready in the rail.
		$scope.loadCategory();
		loadReorderList();
		loadBackorders();
	},
]);
